import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    X,
    Edit3,
    Calendar,
    Clock,
    Tag,
    User,
    Trash2,
    ListOrdered,
    Users,
    ClipboardList,
    Plus,
    Info,
} from "lucide-react";
import OwnerAvatar from "@/components/OwnerAvatar";

const CAT_COLORS = {
    task: "#FF5500",
    meeting: "#00B0FF",
    training: "#00E676",
    ops: "#FFEA00",
};

/**
 * Read-only event overview shown when an operator clicks an event on the
 * schedule grid. Displays header info + micro-schedule + task org. An
 * EDIT button flips into the ScheduleModal.
 */
export default function EventDetailModal({ event, onClose, onEdit, onDelete }) {
    const [confirmDelete, setConfirmDelete] = useState(false);
    if (!event) return null;

    const cat = (event.category || "task").toLowerCase();
    const color = CAT_COLORS[cat] || "#FF5500";
    const isMulti = event.end_date && event.end_date !== event.date;

    const micro = event.micro_schedule || [];
    const org = event.task_org || [];

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 backdrop-blur-md"
                onClick={onClose}
                data-testid="event-detail-modal"
            >
                <motion.div
                    initial={{ y: 16, scale: 0.98 }}
                    animate={{ y: 0, scale: 1 }}
                    exit={{ y: 16, scale: 0.98 }}
                    onClick={(e) => e.stopPropagation()}
                    className="relative w-[960px] max-w-[92vw] max-h-[90vh] overflow-hidden bg-[#0A0A0A] border-2 flex flex-col"
                    style={{ borderColor: color }}
                >
                    {/* Corner brackets */}
                    <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2" style={{ borderColor: color }} />
                    <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2" style={{ borderColor: color }} />
                    <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2" style={{ borderColor: color }} />
                    <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2" style={{ borderColor: color }} />

                    {/* Header ribbon */}
                    <div className="flex items-center justify-between px-8 py-4 border-b border-virtus-line flex-shrink-0">
                        <div className="flex items-center gap-4">
                            <span
                                className="font-mono text-[10px] tracking-[0.28em] uppercase"
                                style={{ color }}
                            >
                                {cat.toUpperCase()}
                            </span>
                            <span className="text-virtus-line">//</span>
                            <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-dim">
                                {event.date}
                                {isMulti ? ` → ${event.end_date}` : ""}
                                {event.time ? ` · ${event.time}` : ""}
                            </span>
                        </div>
                        <button
                            onClick={onClose}
                            className="flex items-center gap-2 px-3 py-2 font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-dim hover:text-virtus-orange border border-virtus-line hover:border-virtus-orange"
                            data-testid="event-detail-close-btn"
                        >
                            <X size={12} /> CLOSE
                        </button>
                    </div>

                    {/* Scrollable body */}
                    <div className="flex-1 overflow-y-auto">
                        {/* Title */}
                        <div className="px-8 pt-6 pb-3">
                            <h1
                                className="font-heading font-black uppercase tracking-tighter leading-none text-white"
                                style={{ fontSize: "clamp(36px, 4.5vw, 60px)" }}
                            >
                                {event.title}
                            </h1>
                        </div>

                        {/* Details grid */}
                        <div className="grid grid-cols-4 gap-px bg-virtus-line border-t border-b border-virtus-line">
                            <Detail icon={Calendar} label={isMulti ? "Range" : "Date"} value={isMulti ? `${event.date} → ${event.end_date}` : event.date} accent={color} />
                            <Detail icon={Clock} label="Time" value={event.time || "—"} />
                            <Detail icon={Tag} label="Category" value={cat.toUpperCase()} accent={color} />
                            <div className="bg-virtus-bg p-5">
                                <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-faint mb-2">
                                    <User size={12} /> Lead
                                </div>
                                {event.owner ? (
                                    <OwnerAvatar
                                        owner={event.owner}
                                        size={32}
                                        textClassName="text-white font-bold text-base"
                                    />
                                ) : (
                                    <div className="font-mono text-lg font-bold text-virtus-dim">Unassigned</div>
                                )}
                            </div>
                        </div>

                        {/* Notes */}
                        {event.notes && event.notes.trim() && (
                            <div className="px-8 py-5">
                                <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim mb-2">
                                    <Info size={12} /> Notes
                                </div>
                                <div className="text-base leading-relaxed text-white whitespace-pre-wrap">
                                    {event.notes}
                                </div>
                            </div>
                        )}

                        {/* Micro-schedule */}
                        <div className="px-8 py-5 border-t border-virtus-line">
                            <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim mb-4">
                                <ListOrdered size={12} /> Micro-Schedule
                                {micro.length > 0 && (
                                    <span className="font-mono text-[10px] text-virtus-faint">[{micro.length}]</span>
                                )}
                            </div>
                            {micro.length === 0 ? (
                                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-faint">
                                    &gt; No sub-blocks recorded.
                                </div>
                            ) : (
                                <div className="border border-virtus-line divide-y divide-virtus-line">
                                    {micro.map((b) => (
                                        <div
                                            key={b.id}
                                            className="grid grid-cols-[110px_1fr_140px] gap-3 px-4 py-2 items-center"
                                            data-testid={`event-micro-row-${b.id}`}
                                        >
                                            <div
                                                className="font-mono text-sm"
                                                style={{ color }}
                                            >
                                                {b.time || "—"}
                                            </div>
                                            <div>
                                                <div className="text-white">{b.title}</div>
                                                {b.notes && (
                                                    <div className="text-xs text-virtus-dim mt-0.5">{b.notes}</div>
                                                )}
                                            </div>
                                            <div className="justify-self-end">
                                                {b.owner ? (
                                                    <OwnerAvatar owner={b.owner} size={20} />
                                                ) : (
                                                    <span className="font-mono text-xs text-virtus-faint">—</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Task Org */}
                        <div className="px-8 py-5 border-t border-virtus-line">
                            <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim mb-4">
                                <Users size={12} /> Task Organization
                                {org.length > 0 && (
                                    <span className="font-mono text-[10px] text-virtus-faint">[{org.length}]</span>
                                )}
                            </div>
                            {org.length === 0 ? (
                                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-faint">
                                    &gt; No task org recorded.
                                </div>
                            ) : (
                                <div className="border border-virtus-line">
                                    <div className="grid grid-cols-[220px_180px_1fr_1fr] gap-3 px-4 py-2 border-b border-virtus-line font-mono text-[9px] tracking-[0.24em] uppercase text-virtus-faint bg-[#0F0F0F]">
                                        <div>NAME</div>
                                        <div>ROLE</div>
                                        <div>RESPONSIBILITY</div>
                                        <div>INSTRUCTIONS</div>
                                    </div>
                                    <div className="divide-y divide-virtus-line">
                                        {org.map((r) => (
                                            <div
                                                key={r.id}
                                                className="grid grid-cols-[220px_180px_1fr_1fr] gap-3 px-4 py-3 items-start text-sm"
                                                data-testid={`event-org-row-${r.id}`}
                                            >
                                                <div className="min-w-0">
                                                    <OwnerAvatar
                                                        owner={r.name}
                                                        size={24}
                                                        textClassName="text-white font-bold"
                                                    />
                                                </div>
                                                <div className="text-virtus-orange font-mono text-xs uppercase">
                                                    {r.role || "—"}
                                                </div>
                                                <div className="text-white/90 whitespace-pre-wrap">
                                                    {r.responsibility || "—"}
                                                </div>
                                                <div className="text-virtus-dim whitespace-pre-wrap">
                                                    {r.instructions || "—"}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer actions */}
                    <div className="flex items-center justify-between px-8 py-4 border-t border-virtus-line flex-shrink-0">
                        <div>
                            {onDelete && (
                                confirmDelete ? (
                                    <button
                                        onClick={() => onDelete(event)}
                                        className="flex items-center gap-2 font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-stuck border border-virtus-stuck px-3 py-2 hover:bg-virtus-stuck hover:text-black"
                                        data-testid="event-detail-confirm-delete-btn"
                                    >
                                        <Trash2 size={11} /> CONFIRM DELETE
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => setConfirmDelete(true)}
                                        className="flex items-center gap-2 font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-faint hover:text-virtus-stuck px-3 py-2"
                                        data-testid="event-detail-delete-btn"
                                    >
                                        <Trash2 size={11} /> DELETE
                                    </button>
                                )
                            )}
                        </div>
                        <button
                            onClick={onEdit}
                            className="flex items-center gap-2 px-4 py-2 font-mono text-[11px] tracking-[0.24em] uppercase bg-virtus-orange text-black hover:bg-white glow-orange"
                            data-testid="event-detail-edit-btn"
                        >
                            <Edit3 size={12} /> EDIT
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

function Detail({ icon: Icon, label, value, accent }) {
    return (
        <div className="bg-virtus-bg p-5">
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-faint mb-2">
                <Icon size={12} />
                {label}
            </div>
            <div className="font-mono text-lg font-bold" style={{ color: accent || "#FFFFFF" }}>
                {value}
            </div>
        </div>
    );
}
